import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  ArrowLeft,
  Save,
  Loader2,
  X,
} from "lucide-react";
import { useAdminAuth, API_BASE } from "@/admin/AdminAuthContext";

const EMPTY = {
  slug: "",
  title: "",
  excerpt: "",
  body: "",
  category: "Insights",
  reading_minutes: 5,
  author: "Marco S. Equis",
};

/** Markdown-lite preview matching the public InsightDetail renderer. */
const renderPreview = (body) => {
  if (!body) return <em className="text-[#F9F6F0]/40">Escribe el contenido para verlo aquí.</em>;
  return body.split(/\n\n+/).map((block, i) => {
    if (block.startsWith("## ")) {
      return (
        <h3
          key={i}
          className="font-display text-xl font-light mt-6 mb-3 text-[#E6D5B8]"
        >
          {block.replace(/^##\s+/, "")}
        </h3>
      );
    }
    const parts = block.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p
        key={i}
        className="text-sm leading-relaxed text-[#F9F6F0]/75 font-light my-3"
      >
        {parts.map((p, j) =>
          p.startsWith("**") && p.endsWith("**") ? (
            <strong key={j} className="text-[#F9F6F0] font-semibold">
              {p.slice(2, -2)}
            </strong>
          ) : (
            <span key={j}>{p}</span>
          )
        )}
      </p>
    );
  });
};

const slugify = (text) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 100);

const PostForm = ({ initial, onCancel, onSaved }) => {
  const { authedAxios } = useAdminAuth();
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const isEdit = !!initial;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const autoSlug = () => {
    if (!form.title || slugEdited) return;
    update("slug", slugify(form.title));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        reading_minutes: Number(form.reading_minutes) || 5,
      };
      if (isEdit) {
        await authedAxios.put(`${API_BASE}/posts/${initial.slug}`, payload);
        toast.success("Insight actualizado");
      } else {
        await authedAxios.post(`${API_BASE}/posts`, payload);
        toast.success("Insight publicado");
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error al guardar";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid lg:grid-cols-2 gap-8" data-testid="post-form">
      {/* Editor */}
      <div className="space-y-5">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase text-[#A397B4] hover:text-[#E6D5B8]"
        >
          <ArrowLeft size={13} />
          <span>Cancelar</span>
        </button>

        <div>
          <label className="block text-[10px] tracking-luxury uppercase text-[#A397B4] mb-2">
            Título
          </label>
          <input
            data-testid="post-input-title"
            required
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            onBlur={() => !form.slug && autoSlug()}
            placeholder="Título del insight"
            className="w-full bg-[#150A1F] border border-[#2A0A4A] focus:border-[#E6D5B8] outline-none rounded-sm px-4 py-3 text-base text-[#F9F6F0]"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] tracking-luxury uppercase text-[#A397B4] mb-2">
              Slug (URL)
            </label>
            <div className="relative">
              <input
                data-testid="post-input-slug"
                required
                value={form.slug}
                onChange={(e) => {
                  setSlugEdited(true);
                  update(
                    "slug",
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, "")
                  );
                }}
                placeholder="mi-insight"
                disabled={isEdit}
                className="w-full bg-[#150A1F] border border-[#2A0A4A] focus:border-[#E6D5B8] outline-none rounded-sm px-4 py-3 text-sm text-[#F9F6F0] disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {!isEdit && (
                <button
                  type="button"
                  onClick={autoSlug}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] tracking-luxury uppercase text-[#A397B4] hover:text-[#E6D5B8] px-2"
                >
                  Auto
                </button>
              )}
            </div>
            {isEdit && (
              <p className="text-[10px] text-[#A397B4]/65 mt-1.5">
                No editable después de publicar.
              </p>
            )}
          </div>
          <div>
            <label className="block text-[10px] tracking-luxury uppercase text-[#A397B4] mb-2">
              Categoría
            </label>
            <input
              data-testid="post-input-category"
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              className="w-full bg-[#150A1F] border border-[#2A0A4A] focus:border-[#E6D5B8] outline-none rounded-sm px-4 py-3 text-sm text-[#F9F6F0]"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] tracking-luxury uppercase text-[#A397B4] mb-2">
              Lectura (min)
            </label>
            <input
              data-testid="post-input-minutes"
              type="number"
              min="1"
              max="60"
              value={form.reading_minutes}
              onChange={(e) => update("reading_minutes", e.target.value)}
              className="w-full bg-[#150A1F] border border-[#2A0A4A] focus:border-[#E6D5B8] outline-none rounded-sm px-4 py-3 text-sm text-[#F9F6F0]"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-luxury uppercase text-[#A397B4] mb-2">
              Autor
            </label>
            <input
              data-testid="post-input-author"
              value={form.author}
              onChange={(e) => update("author", e.target.value)}
              className="w-full bg-[#150A1F] border border-[#2A0A4A] focus:border-[#E6D5B8] outline-none rounded-sm px-4 py-3 text-sm text-[#F9F6F0]"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] tracking-luxury uppercase text-[#A397B4] mb-2">
            Resumen (excerpt)
          </label>
          <textarea
            data-testid="post-input-excerpt"
            required
            value={form.excerpt}
            onChange={(e) => update("excerpt", e.target.value)}
            rows={3}
            placeholder="Texto corto que aparecerá en la lista y como subtítulo del artículo."
            className="w-full bg-[#150A1F] border border-[#2A0A4A] focus:border-[#E6D5B8] outline-none rounded-sm px-4 py-3 text-sm text-[#F9F6F0] resize-y"
          />
        </div>

        <div>
          <label className="block text-[10px] tracking-luxury uppercase text-[#A397B4] mb-2">
            Contenido
            <span className="ml-3 normal-case text-[#A397B4]/60 tracking-normal">
              · usa <code>## Subtítulo</code> y <code>**negrita**</code>. Separa párrafos con línea en blanco.
            </span>
          </label>
          <textarea
            data-testid="post-input-body"
            required
            value={form.body}
            onChange={(e) => update("body", e.target.value)}
            rows={16}
            placeholder={"Escribe aquí el contenido completo del artículo.\n\nUsa dos saltos de línea para separar párrafos.\n\n## Un subtítulo\n\nMás contenido..."}
            className="w-full bg-[#150A1F] border border-[#2A0A4A] focus:border-[#E6D5B8] outline-none rounded-sm px-4 py-3 text-sm text-[#F9F6F0] resize-y font-mono leading-relaxed"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          data-testid="post-form-submit"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#E6D5B8] hover:bg-[#FBEFD3] disabled:opacity-60 text-[#0B0410] px-8 py-3.5 text-xs tracking-luxury uppercase font-semibold rounded-sm transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          <span>{saving ? "Guardando..." : isEdit ? "Actualizar insight" : "Publicar insight"}</span>
        </button>
      </div>

      {/* Preview */}
      <div className="lg:sticky lg:top-10 self-start">
        <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-3 flex items-center gap-2">
          <Eye size={11} />
          <span>Vista previa en vivo</span>
        </div>
        <div className="bg-[#0E0518] border border-[#2A0A4A] rounded-sm p-7 max-h-[80vh] overflow-y-auto">
          <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] bg-[#3D1565]/30 border border-[#3D1565] px-2.5 py-1 rounded-sm inline-block mb-4">
            {form.category || "—"}
          </div>
          <h1 className="font-display text-2xl lg:text-3xl font-light leading-tight">
            {form.title || (
              <em className="text-[#F9F6F0]/35">Título aparecerá aquí</em>
            )}
          </h1>
          <p className="text-sm text-[#F9F6F0]/65 mt-3 italic font-light">
            {form.excerpt || (
              <em className="text-[#F9F6F0]/30">El resumen aparecerá aquí.</em>
            )}
          </p>
          <div className="text-[10px] text-[#A397B4] mt-4 pb-4 border-b border-white/10">
            {form.author} · {form.reading_minutes || 1} min
          </div>
          <div className="pt-2">{renderPreview(form.body)}</div>
        </div>
      </div>
    </form>
  );
};

const AdminPosts = () => {
  const { authedAxios } = useAdminAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("list"); // list | new | edit
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await authedAxios.get(`${API_BASE}/posts?limit=100`);
      setPosts(res.data || []);
    } catch {
      toast.error("No pudimos cargar los insights");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaved = async () => {
    setMode("list");
    setEditing(null);
    await refresh();
  };

  const handleDelete = async (slug) => {
    try {
      await authedAxios.delete(`${API_BASE}/posts/${slug}`);
      toast.success("Insight eliminado");
      setConfirmDelete(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const sorted = useMemo(
    () =>
      [...posts].sort((a, b) =>
        (b.published_at || "").localeCompare(a.published_at || "")
      ),
    [posts]
  );

  if (mode === "new" || mode === "edit") {
    return (
      <div className="max-w-7xl mx-auto" data-testid="admin-posts-form-view">
        <h1 className="font-display text-3xl lg:text-4xl font-light mb-8">
          {mode === "edit" ? "Editar insight" : "Nuevo insight"}
        </h1>
        <PostForm
          initial={mode === "edit" ? editing : null}
          onCancel={() => {
            setMode("list");
            setEditing(null);
          }}
          onSaved={handleSaved}
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-posts-list-view">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
            Insights
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-light leading-tight">
            Gestión de{" "}
            <span className="italic text-[#E6D5B8]">contenido editorial</span>
          </h1>
        </div>
        <button
          onClick={() => setMode("new")}
          data-testid="post-new-btn"
          className="inline-flex items-center gap-2 bg-[#E6D5B8] hover:bg-[#FBEFD3] text-[#0B0410] px-6 py-3 text-xs tracking-luxury uppercase font-semibold rounded-sm"
        >
          <Plus size={14} />
          <span>Nuevo insight</span>
        </button>
      </div>

      {loading && (
        <div className="text-sm text-[#F9F6F0]/50 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando...
        </div>
      )}

      {!loading && sorted.length === 0 && (
        <div className="bg-[#150A1F] border border-[#2A0A4A] rounded-sm p-10 text-center">
          <p className="text-sm text-[#F9F6F0]/65 mb-5">
            Aún no hay insights publicados.
          </p>
          <button
            onClick={() => setMode("new")}
            className="inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase text-[#E6D5B8] hover:text-[#FBEFD3]"
          >
            <Plus size={12} /> Crear el primero
          </button>
        </div>
      )}

      {!loading && sorted.length > 0 && (
        <div className="bg-[#150A1F] border border-[#2A0A4A] rounded-sm divide-y divide-white/[0.07]">
          {sorted.map((p) => (
            <div
              key={p.slug}
              data-testid={`post-row-${p.slug}`}
              className="flex flex-col md:flex-row md:items-center gap-4 p-5 md:p-6 hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <span className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] bg-[#3D1565]/30 border border-[#3D1565] px-2.5 py-1 rounded-sm">
                    {p.category}
                  </span>
                  <span className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
                    {p.published_at}
                  </span>
                  <span className="text-[10px] text-[#A397B4]">
                    · {p.reading_minutes} min
                  </span>
                </div>
                <div className="font-display text-lg lg:text-xl leading-tight truncate">
                  {p.title}
                </div>
                <div className="text-[11px] text-[#A397B4]/85 mt-1 truncate">
                  /insights/{p.slug}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={`/insights/${p.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`post-view-${p.slug}`}
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-luxury uppercase text-[#A397B4] hover:text-[#E6D5B8] px-3 py-2 border border-white/10 hover:border-[#E6D5B8]/40 rounded-sm transition-colors"
                  title="Ver publicado"
                >
                  <Eye size={12} /> Ver
                </a>
                <button
                  onClick={() => {
                    setEditing(p);
                    setMode("edit");
                  }}
                  data-testid={`post-edit-${p.slug}`}
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-luxury uppercase text-[#F9F6F0] hover:text-[#E6D5B8] px-3 py-2 border border-white/10 hover:border-[#E6D5B8]/40 rounded-sm transition-colors"
                >
                  <Pencil size={12} /> Editar
                </button>
                <button
                  onClick={() => setConfirmDelete(p)}
                  data-testid={`post-delete-${p.slug}`}
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-luxury uppercase text-red-300 hover:text-red-200 px-3 py-2 border border-red-500/20 hover:border-red-500/50 rounded-sm transition-colors"
                >
                  <Trash2 size={12} /> Borrar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirm modal */}
      {confirmDelete && (
        <div
          data-testid="post-delete-confirm"
          className="fixed inset-0 z-50 bg-[#0B0410]/85 backdrop-blur-md flex items-center justify-center px-6"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#150A1F] border border-[#2A0A4A] rounded-sm max-w-md w-full p-7"
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-display text-xl">Eliminar insight</h3>
              <button
                onClick={() => setConfirmDelete(null)}
                className="text-[#A397B4] hover:text-[#F9F6F0]"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-sm text-[#F9F6F0]/70 mb-2">
              ¿Seguro que quieres eliminar{" "}
              <span className="text-[#E6D5B8] italic">
                "{confirmDelete.title}"
              </span>
              ?
            </p>
            <p className="text-xs text-[#A397B4]/70">
              Esta acción no se puede deshacer.
            </p>
            <div className="mt-7 flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-5 py-2.5 text-[11px] tracking-luxury uppercase text-[#F9F6F0]/80 hover:text-[#F9F6F0] border border-white/10 hover:border-white/30 rounded-sm transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(confirmDelete.slug)}
                data-testid="post-delete-confirm-btn"
                className="px-5 py-2.5 text-[11px] tracking-luxury uppercase bg-red-500/80 hover:bg-red-500 text-white rounded-sm transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPosts;
